import React, { useCallback, useEffect, useState } from 'react';
import AuthService from '../../services/AuthService';
import CrmWorkflowService from '../../services/CrmWorkflowService';
import PropertyService from '../../services/PropertyService';
import RegisterUserService from '../../services/RegisterUserService';
import LeadsAssignedService from '../../services/LeadsAssignedService';
import {
  VISIT_STATUSES,
  DEAL_STAGES,
  getVisitStatus,
  getDealStage,
} from '../../constants/crmWorkflowConstants';
import { fmtPrice } from '../../constants/propertyConstants';

const toInputDateTime = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1) return '';
  return d.toISOString().slice(0, 16);
};

const propertyLabel = (p) => {
  if (!p) return '—';
  const bits = [p.projectName, p.towerBlock, p.unitNumber].filter(Boolean);
  const loc = bits.length ? bits.join(' · ') : '';
  const base = [p.propertyName, loc].filter(Boolean).join(' — ') || `#${p.id}`;
  return p.inventorySource === 'ChannelPartner' ? `${base} (CP)` : base;
};

const lbl = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: '#8392ab',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inp = {
  width: '100%',
  padding: '8px 12px',
  border: '1.5px solid #e9ecef',
  borderRadius: '8px',
  fontSize: '13px',
  color: '#344767',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary = {
  background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: '700',
  color: '#fff',
  cursor: 'pointer',
};

const btnGhost = {
  background: '#fff',
  border: '1.5px solid #e9ecef',
  borderRadius: '8px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: '600',
  color: '#525f7f',
  cursor: 'pointer',
};

/** Tenant users eligible to appear as buyer owner: Admins (role 2, and legacy JWT 3) and Employees. */
function normalizeSalesStaff(rawList, options = {}) {
  const { ensureUserId } = options;
  const arr = Array.isArray(rawList) ? rawList : [];
  const SALES_ROLES = new Set([2, 3, 4]);
  const uid = (u) => Number(u.id ?? u.Id);
  let list = arr
    .filter((u) => {
      const active = (u.isActive ?? u.IsActive) !== false;
      const rid = Number(u.roleId ?? u.RoleId ?? 0);
      return active && SALES_ROLES.has(rid);
    })
    .sort((a, b) => {
      const na = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || '';
      const nb = [b.firstName, b.lastName].filter(Boolean).join(' ') || b.email || '';
      return na.localeCompare(nb, undefined, { sensitivity: 'base' });
    });
  if (ensureUserId != null && ensureUserId !== '' && !Number.isNaN(Number(ensureUserId))) {
    const idNum = Number(ensureUserId);
    if (!list.some((u) => uid(u) === idNum)) {
      const extra = arr.find((u) => uid(u) === idNum);
      if (extra) list = [...list, extra];
    }
  }
  return list;
}

/**
 * Lead ↔ inventory mapping, buyer conversion, and deal pipeline for one lead.
 */
export default function LeadPropertyDealPanel({ leadId, leadAssignedId, toastService }) {
  const clientId = AuthService.getLoggedInClientId();
  const salesUserId = AuthService.getLoggedInUserId();

  const [loading, setLoading] = useState(true);
  const [interests, setInterests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [buyer, setBuyer] = useState(null);
  const [salesStaff, setSalesStaff] = useState([]);
  const [deals, setDeals] = useState([]);
  const [addPropertyId, setAddPropertyId] = useState('');
  const [buyerDraft, setBuyerDraft] = useState({});
  const [newDeal, setNewDeal] = useState({
    propertyId: '',
    dealValue: '',
    stage: 'Negotiation',
    expectedCloseDate: '',
    notes: '',
  });
  const [lostDraft, setLostDraft] = useState({});

  const refreshDeals = useCallback(async (buyerId) => {
    if (!buyerId) {
      setDeals([]);
      return;
    }
    const list = await CrmWorkflowService.getDealsByBuyer(buyerId);
    setDeals(list);
  }, []);

  const load = useCallback(async () => {
    if (!leadId || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const cid = Number(clientId);
      const laNum =
        leadAssignedId === undefined || leadAssignedId === null || leadAssignedId === ''
          ? NaN
          : Number(leadAssignedId);
      const assignmentPromise =
        !Number.isNaN(laNum) && laNum > 0
          ? LeadsAssignedService.getLeadsAssignedById(laNum).catch(() => null)
          : Promise.resolve(null);

      const [intList, propsList, buyerRow, staffRaw, assignmentRow] = await Promise.all([
        CrmWorkflowService.getInterestsByLead(leadId),
        PropertyService.getPropertiesByClientId(clientId),
        CrmWorkflowService.getBuyerByLead(leadId),
        RegisterUserService.getUsersByClientId(cid).catch(() => []),
        assignmentPromise,
      ]);
      setInterests(intList);
      setProperties(propsList);
      setBuyer(buyerRow);

      const buyerAssignedUid = buyerRow?.assignedUserId ?? buyerRow?.AssignedUserId;
      const hasBuyerAssigned =
        buyerAssignedUid !== null && buyerAssignedUid !== undefined && buyerAssignedUid !== '';
      const leadAssigneeUid = assignmentRow?.userId ?? assignmentRow?.UserId;
      const effectiveAssignedUid = hasBuyerAssigned ? buyerAssignedUid : leadAssigneeUid;

      setSalesStaff(normalizeSalesStaff(staffRaw, { ensureUserId: effectiveAssignedUid }));
      setBuyerDraft({
        firstName: buyerRow?.firstName ?? buyerRow?.FirstName ?? '',
        lastName: buyerRow?.lastName ?? buyerRow?.LastName ?? '',
        email: buyerRow?.email ?? buyerRow?.Email ?? '',
        phone: buyerRow?.phone ?? buyerRow?.Phone ?? '',
        budgetMin: buyerRow?.budgetMin ?? buyerRow?.BudgetMin ?? '',
        budgetMax: buyerRow?.budgetMax ?? buyerRow?.BudgetMax ?? '',
        requirementNotes: buyerRow?.requirementNotes ?? buyerRow?.RequirementNotes ?? '',
        financingNotes: buyerRow?.financingNotes ?? buyerRow?.FinancingNotes ?? '',
        assignedUserId:
          effectiveAssignedUid === null ||
          effectiveAssignedUid === undefined ||
          effectiveAssignedUid === ''
            ? ''
            : String(effectiveAssignedUid),
      });
      await refreshDeals(buyerRow?.id);
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', 'Could not load CRM workflow data.');
    } finally {
      setLoading(false);
    }
  }, [leadId, leadAssignedId, clientId, toastService, refreshDeals]);

  useEffect(() => {
    load();
  }, [load]);

  const linkedIds = new Set(interests.map((i) => i.propertyId));
  const availableToLink = properties.filter((p) => p.id && !linkedIds.has(p.id));

  const upsertRow = async (row) => {
    try {
      const dto = {
        leadId,
        leadAssignedId: leadAssignedId || null,
        propertyId: row.propertyId,
        visitStatus: row.visitStatus || 'NotVisited',
        scheduledVisitDate: row.scheduledVisitDate
          ? new Date(row.scheduledVisitDate).toISOString()
          : null,
        actualVisitDate: row.actualVisitDate ? new Date(row.actualVisitDate).toISOString() : null,
        notes: row.notes || '',
      };
      await CrmWorkflowService.upsertInterest(dto);
      toastService?.showSuccess?.('Saved', 'Property interest updated.');
      await load();
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', e.response?.data?.message || e.message || 'Save failed.');
    }
  };

  const removeRow = async (rowId) => {
    if (!window.confirm('Remove this property from the lead?')) return;
    try {
      await CrmWorkflowService.deleteInterest(rowId);
      toastService?.showSuccess?.('Removed', 'Link removed.');
      await load();
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', 'Could not remove link.');
    }
  };

  const addLink = async () => {
    const pid = parseInt(addPropertyId, 10);
    if (!pid) {
      toastService?.showError?.('Validation', 'Select a property.');
      return;
    }
    setAddPropertyId('');
    await upsertRow({
      propertyId: pid,
      visitStatus: 'NotVisited',
      scheduledVisitDate: null,
      actualVisitDate: null,
      notes: '',
    });
  };

  const convertBuyer = async () => {
    try {
      const b = await CrmWorkflowService.convertBuyerFromLead(leadId);
      setBuyer(b);
      toastService?.showSuccess?.('Buyer profile', 'Lead converted to buyer (qualified profile).');
      await load();
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', e.response?.data || 'Conversion failed.');
    }
  };

  const saveBuyer = async () => {
    if (!buyer?.id) return;
    try {
      await CrmWorkflowService.updateBuyer({
        id: buyer.id ?? buyer.Id,
        clientId: buyer.clientId ?? buyer.ClientId,
        sourceLeadId: buyer.sourceLeadId ?? buyer.SourceLeadId,
        assignedUserId:
          buyerDraft.assignedUserId === '' || buyerDraft.assignedUserId == null
            ? null
            : Number(buyerDraft.assignedUserId),
        firstName: buyerDraft.firstName || '',
        lastName: buyerDraft.lastName || '',
        email: buyerDraft.email || '',
        phone: buyerDraft.phone || '',
        budgetMin: buyerDraft.budgetMin === '' ? null : Number(buyerDraft.budgetMin),
        budgetMax: buyerDraft.budgetMax === '' ? null : Number(buyerDraft.budgetMax),
        requirementNotes: buyerDraft.requirementNotes || '',
        financingNotes: buyerDraft.financingNotes || '',
        preferencesJson: buyer.preferencesJson ?? buyer.PreferencesJson ?? null,
        documentsJson: buyer.documentsJson ?? buyer.DocumentsJson ?? null,
      });
      toastService?.showSuccess?.('Saved', 'Buyer profile updated.');
      await load();
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', 'Could not update buyer.');
    }
  };

  const createDeal = async () => {
    const propId = parseInt(newDeal.propertyId, 10);
    const val = newDeal.dealValue === '' ? 0 : Number(newDeal.dealValue);
    if (!buyer?.id || !propId) {
      toastService?.showError?.('Validation', 'Create a buyer first and pick a property.');
      return;
    }
    try {
      await CrmWorkflowService.createDeal({
        clientId,
        buyerId: buyer.id,
        propertyId: propId,
        salesUserId,
        leadAssignedId: leadAssignedId || null,
        stage: newDeal.stage || 'Negotiation',
        dealValue: val,
        expectedCloseDate: newDeal.expectedCloseDate
          ? new Date(newDeal.expectedCloseDate).toISOString()
          : null,
        notes: newDeal.notes || '',
      });
      toastService?.showSuccess?.('Deal', 'Deal created.');
      setNewDeal({
        propertyId: '',
        dealValue: '',
        stage: 'Negotiation',
        expectedCloseDate: '',
        notes: '',
      });
      await refreshDeals(buyer.id);
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', e.response?.data || 'Could not create deal.');
    }
  };

  const patchDealStage = async (dealRow, stage, explicitLostReason) => {
    const lr =
      stage === 'ClosedLost'
        ? (explicitLostReason !== undefined && explicitLostReason !== null
            ? explicitLostReason
            : lostDraft[dealRow.id] || '')
        : '';
    try {
      await CrmWorkflowService.updateDealStage(dealRow.id, stage, lr);
      toastService?.showSuccess?.('Stage', 'Deal stage updated.');
      await refreshDeals(buyer.id);
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', 'Stage update failed.');
    }
  };

  const removeDeal = async (dealId) => {
    if (!window.confirm('Delete this deal?')) return;
    try {
      await CrmWorkflowService.deleteDeal(dealId);
      toastService?.showSuccess?.('Removed', 'Deal deleted.');
      await refreshDeals(buyer.id);
    } catch (e) {
      console.error(e);
      toastService?.showError?.('Error', 'Could not delete deal.');
    }
  };

  if (!clientId) {
    return (
      <div style={{ padding: '24px', color: '#8392ab', fontSize: '14px' }}>
        Your account has no client context. Sign in as a tenant user to use inventory matching and deals.
      </div>
    );
  }

  if (!leadId) {
    return (
      <div style={{ padding: '24px', color: '#8392ab', fontSize: '14px' }}>
        Loading assignment…
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#8392ab' }}>
        <i className="fas fa-circle-notch fa-spin" style={{ marginRight: '8px' }} />
        Loading properties & deals…
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      <p style={{ fontSize: '13px', color: '#8392ab', marginBottom: '24px', lineHeight: 1.6 }}>
        Match this lead to one or more inventory units, track site visits, convert to a{' '}
        <strong>buyer profile</strong>, then run the sale as a <strong>deal</strong> (Buyer + Property + stage).
        Inventory stays independent — links are flexible until you commit on a deal.
      </p>

      {/* Lead ↔ Property */}
      <section style={{ marginBottom: '32px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '700',
              color: '#344767',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              flex: '0 0 auto',
            }}
          >
            <i className="fas fa-link" style={{ marginRight: '8px', color: '#5e72e4' }} />
            Inventory matching
          </h3>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              flex: '1 1 280px',
            }}
          >
            <select
              style={{ ...inp, minWidth: '200px', flex: '1 1 200px', maxWidth: '420px' }}
              value={addPropertyId}
              onChange={(e) => setAddPropertyId(e.target.value)}
            >
              <option value="">— Add property —</option>
              {availableToLink.map((p) => (
                <option key={p.id} value={p.id}>
                  {propertyLabel(p)}
                </option>
              ))}
            </select>
            <button type="button" style={{ ...btnPrimary, whiteSpace: 'nowrap' }} onClick={addLink}>
              <i className="fas fa-plus" style={{ marginRight: '6px' }} />
              Link
            </button>
          </div>
        </div>

        {interests.length === 0 ? (
          <div style={{ padding: '28px', background: '#f8f9fe', borderRadius: '12px', textAlign: 'center', color: '#8392ab', fontSize: '13px' }}>
            No properties linked yet. Add matches from your inventory — one lead can explore many units.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1.5px solid #f0f2f5', borderRadius: '12px' }}>
            <table className="table table-hover mb-0" style={{ fontSize: '13px', margin: 0 }}>
              <thead style={{ background: '#f8f9fe', color: '#8392ab', fontSize: '11px', textTransform: 'uppercase' }}>
                <tr>
                  <th style={{ padding: '12px 14px' }}>Property</th>
                  <th style={{ padding: '12px 14px' }}>Visit</th>
                  <th style={{ padding: '12px 14px' }}>Scheduled</th>
                  <th style={{ padding: '12px 14px' }}>Visited</th>
                  <th style={{ padding: '12px 14px' }}>Notes</th>
                  <th style={{ padding: '12px 14px', width: '140px' }} />
                </tr>
              </thead>
              <tbody>
                {interests.map((row) => {
                  const vs = getVisitStatus(row.visitStatus);
                  return (
                    <tr key={row.id}>
                      <td style={{ padding: '12px 14px', fontWeight: '600', color: '#344767' }}>
                        {row.projectLabel || row.propertyName ? (
                          <>
                            <div>{row.propertyName}</div>
                            {row.projectLabel && (
                              <div style={{ fontSize: '11px', color: '#8392ab', fontWeight: '500' }}>{row.projectLabel}</div>
                            )}
                          </>
                        ) : (
                          propertyLabel(properties.find((p) => p.id === row.propertyId))
                        )}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <select
                          style={inp}
                          value={row.visitStatus || 'NotVisited'}
                          onChange={(e) =>
                            setInterests((prev) =>
                              prev.map((x) =>
                                x.id === row.id ? { ...x, visitStatus: e.target.value } : x,
                              ),
                            )
                          }
                        >
                          {VISIT_STATUSES.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.label}
                            </option>
                          ))}
                        </select>
                        <span
                          style={{
                            display: 'inline-block',
                            marginTop: '4px',
                            fontSize: '10px',
                            fontWeight: '700',
                            color: vs.color,
                          }}
                        >
                          {vs.label}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', minWidth: '160px' }}>
                        <input
                          type="datetime-local"
                          style={inp}
                          value={toInputDateTime(row.scheduledVisitDate)}
                          onChange={(e) =>
                            setInterests((prev) =>
                              prev.map((x) =>
                                x.id === row.id
                                  ? { ...x, scheduledVisitDate: e.target.value ? new Date(e.target.value).toISOString() : null }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td style={{ padding: '10px 14px', minWidth: '160px' }}>
                        <input
                          type="datetime-local"
                          style={inp}
                          value={toInputDateTime(row.actualVisitDate)}
                          onChange={(e) =>
                            setInterests((prev) =>
                              prev.map((x) =>
                                x.id === row.id
                                  ? { ...x, actualVisitDate: e.target.value ? new Date(e.target.value).toISOString() : null }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td style={{ padding: '10px 14px', minWidth: '140px' }}>
                        <input
                          type="text"
                          style={inp}
                          placeholder="Visit notes"
                          value={row.notes || ''}
                          onChange={(e) =>
                            setInterests((prev) =>
                              prev.map((x) => (x.id === row.id ? { ...x, notes: e.target.value } : x)),
                            )
                          }
                        />
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <button type="button" style={{ ...btnPrimary, padding: '6px 10px', marginRight: '6px' }} onClick={() => upsertRow(row)}>
                          Save
                        </button>
                        <button type="button" style={{ ...btnGhost, padding: '6px 10px', color: '#f5365c', borderColor: '#fce8ee' }} onClick={() => removeRow(row.id)}>
                          <i className="fas fa-unlink" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Buyer */}
      <section style={{ marginBottom: '32px', padding: '20px', background: '#f8f9fe', borderRadius: '14px', border: '1.5px solid #f0f2f5' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#344767', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <i className="fas fa-user-check" style={{ marginRight: '8px', color: '#2dce89' }} />
            Buyer profile (qualified)
          </h3>
          {!buyer?.id && (
            <button type="button" style={btnPrimary} onClick={convertBuyer}>
              Convert lead → buyer
            </button>
          )}
        </div>

        {buyer?.id ? (
          <>
            <div className="row g-3">
              <div className="col-md-3">
                <label style={lbl}>First name</label>
                <input style={inp} value={buyerDraft.firstName} onChange={(e) => setBuyerDraft((d) => ({ ...d, firstName: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label style={lbl}>Last name</label>
                <input style={inp} value={buyerDraft.lastName} onChange={(e) => setBuyerDraft((d) => ({ ...d, lastName: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label style={lbl}>Email</label>
                <input style={inp} type="email" value={buyerDraft.email} onChange={(e) => setBuyerDraft((d) => ({ ...d, email: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label style={lbl}>Phone</label>
                <input style={inp} value={buyerDraft.phone} onChange={(e) => setBuyerDraft((d) => ({ ...d, phone: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label
                  style={lbl}
                  title="Uses this assigned lead’s salesperson when empty on the buyer; change here and save to update the buyer record."
                >
                  Assigned to
                </label>
                <select
                  style={{ ...inp, cursor: 'pointer' }}
                  value={
                    buyerDraft.assignedUserId === '' || buyerDraft.assignedUserId == null
                      ? ''
                      : String(buyerDraft.assignedUserId)
                  }
                  onChange={(e) => setBuyerDraft((d) => ({ ...d, assignedUserId: e.target.value }))}
                >
                  <option value="">— Not assigned —</option>
                  {salesStaff.map((u) => {
                    const sid = u.id ?? u.Id;
                    return (
                      <option key={sid} value={String(sid)}>
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `User #${sid}`}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col-md-3">
                <label style={lbl}>Budget min</label>
                <input style={inp} type="number" value={buyerDraft.budgetMin} onChange={(e) => setBuyerDraft((d) => ({ ...d, budgetMin: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <label style={lbl}>Budget max</label>
                <input style={inp} type="number" value={buyerDraft.budgetMax} onChange={(e) => setBuyerDraft((d) => ({ ...d, budgetMax: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label style={lbl}>Requirements</label>
                <input style={inp} value={buyerDraft.requirementNotes} onChange={(e) => setBuyerDraft((d) => ({ ...d, requirementNotes: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label style={lbl}>Financing</label>
                <input style={inp} value={buyerDraft.financingNotes} onChange={(e) => setBuyerDraft((d) => ({ ...d, financingNotes: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" style={{ ...btnPrimary, whiteSpace: 'nowrap', background: '#2dce89' }} onClick={saveBuyer}>
                <i className="fas fa-save" style={{ marginRight: '6px' }} />
                Save buyer profile
              </button>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: '13px', color: '#8392ab' }}>
            When this lead is serious (budget, requirement, visits), convert to a buyer profile. Deals are always tied to a buyer + property, not raw inquiries alone.
          </p>
        )}
      </section>

      {/* Deals */}
      <section>
        <h3 style={{ marginBottom: '16px', fontSize: '14px', fontWeight: '700', color: '#344767', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <i className="fas fa-handshake" style={{ marginRight: '8px', color: '#fb6340' }} />
          Deals (pipeline)
        </h3>

        {!buyer?.id ? (
          <p style={{ fontSize: '13px', color: '#8392ab' }}>Create a buyer profile to open deals.</p>
        ) : (
          <>
            <div className="row g-3 align-items-end" style={{ marginBottom: '20px', padding: '16px', background: '#fff', borderRadius: '12px', border: '1.5px solid #f0f2f5' }}>
              <div className="col-lg-4 col-md-6">
                <label style={lbl}>Property</label>
                <select style={inp} value={newDeal.propertyId} onChange={(e) => setNewDeal((d) => ({ ...d, propertyId: e.target.value }))}>
                  <option value="">— Select —</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {propertyLabel(p)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-2 col-md-3">
                <label style={lbl}>Value</label>
                <input style={inp} type="number" placeholder="0" value={newDeal.dealValue} onChange={(e) => setNewDeal((d) => ({ ...d, dealValue: e.target.value }))} />
              </div>
              <div className="col-lg-3 col-md-5">
                <label style={lbl}>Stage</label>
                <select style={inp} value={newDeal.stage} onChange={(e) => setNewDeal((d) => ({ ...d, stage: e.target.value }))}>
                  {DEAL_STAGES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-3 col-md-6">
                <label style={lbl}>Expected close</label>
                <input style={inp} type="date" value={newDeal.expectedCloseDate} onChange={(e) => setNewDeal((d) => ({ ...d, expectedCloseDate: e.target.value }))} />
              </div>
              <div className="col-lg-9 col-md-8">
                <label style={lbl}>Notes</label>
                <input style={inp} value={newDeal.notes} onChange={(e) => setNewDeal((d) => ({ ...d, notes: e.target.value }))} />
              </div>
              <div className="col-lg-3 col-md-4 d-flex justify-content-lg-end align-items-end">
                <button
                  type="button"
                  style={{
                    ...btnPrimary,
                    padding: '6px 14px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    marginTop: '22px',
                    width: '100%',
                    maxWidth: '140px',
                  }}
                  onClick={createDeal}
                >
                  Create deal
                </button>
              </div>
            </div>

            {deals.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#8392ab' }}>No deals yet — create one per property you are actively negotiating.</p>
            ) : (
              <div style={{ overflowX: 'auto', border: '1.5px solid #f0f2f5', borderRadius: '12px' }}>
                <table className="table mb-0" style={{ fontSize: '13px' }}>
                  <thead style={{ background: '#f8f9fe', color: '#8392ab', fontSize: '11px', textTransform: 'uppercase' }}>
                    <tr>
                      <th style={{ padding: '12px 14px' }}>Property</th>
                      <th style={{ padding: '12px 14px' }}>Value</th>
                      <th style={{ padding: '12px 14px' }}>Stage</th>
                      <th style={{ padding: '12px 14px' }}>Lost reason</th>
                      <th style={{ padding: '12px 14px', width: '100px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((d) => {
                      const st = getDealStage(d.stage);
                      return (
                        <tr key={d.id}>
                          <td style={{ padding: '12px 14px', fontWeight: '600' }}>
                            <div>{d.propertyName}</div>
                            {d.projectLabel && <div style={{ fontSize: '11px', color: '#8392ab' }}>{d.projectLabel}</div>}
                            {d.dealSourceType === 'ChannelPartner' && (
                              <div style={{ fontSize: '11px', color: '#825ee4', marginTop: '4px', fontWeight: 600 }}>
                                Partner: {d.builderCompanyName || '—'} · Est. commission{' '}
                                {d.expectedCommissionAmount != null ? fmtPrice(d.expectedCommissionAmount) : '—'} ·{' '}
                                {d.commissionStatus || 'Pending'}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px' }}>{fmtPrice(d.dealValue)}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <select
                              style={inp}
                              value={d.stage || 'Negotiation'}
                              onChange={(e) => {
                                const next = e.target.value;
                                if (next === 'ClosedLost') {
                                  const reason = window.prompt('Lost reason (optional)') || '';
                                  setLostDraft((prev) => ({ ...prev, [d.id]: reason }));
                                  void patchDealStage(d, next, reason);
                                } else {
                                  void patchDealStage(d, next, '');
                                }
                              }}
                            >
                              {DEAL_STAGES.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                            <span style={{ fontSize: '10px', fontWeight: '700', color: st.color }}>{st.label}</span>
                          </td>
                          <td style={{ padding: '10px 14px', maxWidth: '200px' }}>
                            <input
                              style={inp}
                              placeholder="If lost…"
                              value={lostDraft[d.id] ?? d.lostReason ?? ''}
                              onChange={(e) => setLostDraft((prev) => ({ ...prev, [d.id]: e.target.value }))}
                            />
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <button type="button" style={{ ...btnGhost, padding: '6px 10px', color: '#f5365c' }} onClick={() => removeDeal(d.id)}>
                              <i className="fas fa-trash-alt" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
